    import React, { useState, useMemo, useEffect, useRef } from "react";
    import SpreetailHeader from "../layout/SpreetailHeader";
    import { RCAResult, RCAPlan, PlanStep, CalculatedColumn, PivotTableConfig } from "../../../shared/rcaApiService";
    import AnalyticsIcon from "@mui/icons-material/Analytics";
    import CheckCircleIcon from "@mui/icons-material/CheckCircle";
    import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
    import TableChartIcon from "@mui/icons-material/TableChart";
    import PlayArrowIcon from "@mui/icons-material/PlayArrow";
    import JoinInnerIcon from "@mui/icons-material/JoinInner";
    import { renderExcelStyledReport, convertHtmlToJess } from "../Market-Watch/excelStyledRenderer";
    import type { ExcelStyledReport } from "../Market-Watch/excelStyleTypes";
    import { SHEET_NAME_MAX_LENGTH } from "../../../shared/constants";

interface RCAResultsViewProps {
        result: RCAResult;
        userQuestion: string;
        onReset: () => void;
        plan?: RCAPlan | null;
    }

    // Extract base step type from step_id (e.g., "smart_column_2" -> "smart_column")
    const getBaseStepType = (stepId: string): string => {
        return stepId.replace(/_\d+$/, '');
    };

    // Icon mapping for plan steps
    const getStepIcon = (stepId: string) => {
        const baseType = getBaseStepType(stepId);
        switch (baseType) {
            case "smart_column":
                return <AutoFixHighIcon sx={{ fontSize: 18, color: "#068E75" }} />;
            case "pivot_table":
                return <TableChartIcon sx={{ fontSize: 18, color: "#068E75" }} />;
            case "smart_match":
                return <JoinInnerIcon sx={{ fontSize: 18, color: "#068E75" }} />;
            case "rca_analysis":
                return <AnalyticsIcon sx={{ fontSize: 18, color: "#068E75" }} />;
            default:
                return <CheckCircleIcon sx={{ fontSize: 18, color: "#068E75" }} />;
        }
    };

    const getStepTitle = (stepId: string): string => {
        const baseType = getBaseStepType(stepId);
        const match = stepId.match(/_(\d+)$/);
        const suffix = match ? ` (${match[1]})` : '';

        switch (baseType) {
            case "smart_column":
                return `Generate Smart Columns${suffix}`;
            case "pivot_table":
                return `Create Pivot Table${suffix}`;
            case "smart_match":
                return `Smart Match${suffix}`;
            case "rca_analysis":
                return "Analysis";
            default:
                return stepId;
        }
    };


    const RCAResultsView: React.FC<RCAResultsViewProps> = ({
        result,
        userQuestion,
        onReset,
        plan
    }) => {
        const [isWritingData, setIsWritingData] = useState(false);
        const [writeSuccess, setWriteSuccess] = useState<string | null>(null);
        const [writeError, setWriteError] = useState<string | null>(null);
        const writeErrorRef = useRef<HTMLDivElement>(null);

        useEffect(() => {
            if (writeError && writeErrorRef.current) {
                writeErrorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
            }
        }, [writeError]);

        const [executingStep, setExecutingStep] = useState<string | null>(null);
        const [stepResults, setStepResults] = useState<Record<string, "success" | "error" | null>>({});
        const [selectedSteps, setSelectedSteps] = useState<Set<string>>(new Set());
        const [isRenderingAll, setIsRenderingAll] = useState(false);
        // Per-step grid type selection (supports multiple pivot tables)
        const [pivotGridTypes, setPivotGridTypes] = useState<Record<string, "pivot" | "grouped">>({});
        // Track which dropdown is currently open
        const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);

        // Helper to get grid type for a specific step (defaults to "pivot")
        const getGridTypeForStep = (stepId: string): "pivot" | "grouped" => {
            return pivotGridTypes[stepId] || "pivot";
        };

        // Helper to set grid type for a specific step
        const setGridTypeForStep = (stepId: string, gridType: "pivot" | "grouped") => {
            setPivotGridTypes(prev => ({ ...prev, [stepId]: gridType }));
        };

        // Get pivot table data for a specific step_id (supports multiple pivot tables)
        const getPivotTableDataForStep = (stepId: string): {
            config: PivotTableConfig | null;
            groupedGrid: Array<Record<string, any>> | null;
        } => {
            // Check new pivot_table_results structure first
            if (result.pivot_table_results && result.pivot_table_results[stepId]) {
                const stepResult = result.pivot_table_results[stepId];
                return {
                    config: stepResult.config || null,
                    groupedGrid: stepResult.grouped_grid || null
                };
            }

            // Fall back to legacy single pivot_table for backward compatibility
            // Use legacy data for the base "pivot_table" step or if no pivot_table_results exists
            const baseType = getBaseStepType(stepId);
            if (baseType === "pivot_table") {
                return {
                    config: result.pivot_table || null,
                    groupedGrid: result.grouped_grid || null
                };
            }

            return { config: null, groupedGrid: null };
        };

        const sanitizeEnrichedTableData = (headers: string[], rows: any[][]) => {
            const columnScoresIdx = headers.indexOf("column_scores");
            const filteredHeaders = columnScoresIdx >= 0
                ? headers.filter((_, i) => i !== columnScoresIdx)
                : headers;
            const filteredRows = rows.map(row => {
                const filtered = columnScoresIdx >= 0
                    ? row.filter((_, i) => i !== columnScoresIdx)
                    : row;
                return filtered.map(val => {
                    if (val === null || val === undefined) return "";
                    if (typeof val === "object") return JSON.stringify(val);
                    return val;
                });
            });
            return { headers: filteredHeaders, rows: filteredRows };
        };

        const handleWriteToExcel = async () => {
            setIsWritingData(true);
            setWriteError(null);
            setWriteSuccess(null);

            try {
                await Excel.run(async (context) => {
                    const workbook = context.workbook;

                    const sheetName = `Analysis SC ${new Date().toLocaleTimeString().replace(/:/g, "-")}`;
                    const newSheet = workbook.worksheets.add(sheetName);

                    const { headers, rows: data } = sanitizeEnrichedTableData(
                        result.enriched_table.headers,
                        result.enriched_table.rows
                    );
                    const headerRange = newSheet.getRangeByIndexes(0, 0, 1, headers.length);
                    headerRange.values = [headers];
                    headerRange.format.font.bold = true;
                    headerRange.format.fill.color = "#068E75";
                    headerRange.format.font.color = "#FFFFFF";

                    if (data.length > 0) {
                        const dataRange = newSheet.getRangeByIndexes(1, 0, data.length, headers.length);
                        dataRange.values = data;
                    }

                    const fullRange = newSheet.getRangeByIndexes(0, 0, data.length + 1, headers.length);
                    fullRange.format.autofitColumns();

                    const tableRange = newSheet.getRangeByIndexes(0, 0, data.length + 1, headers.length);
                    const table = newSheet.tables.add(tableRange, true);
                    table.name = `RCAData_${Date.now()}`;
                    table.style = "TableStyleMedium2";

                    newSheet.activate();
                    await context.sync();

                    setWriteSuccess(`Data written to sheet "${sheetName}" with ${data.length} rows`);
                });
            } catch (err) {
                console.error("Failed to write to Excel:", err);
                setWriteError(err instanceof Error ? err.message : "Failed to write data to Excel");
            } finally {
                setIsWritingData(false);
            }
        };

        // Helper: Get calculated_columns from pivot_table or pivot_table_results
        // pivot_table_results may have calculated_columns in config even when pivot_table doesn't
        const getCalculatedColumnsFromResult = (): CalculatedColumn[] => {
            // First check legacy pivot_table
            if (result.pivot_table?.calculated_columns && result.pivot_table.calculated_columns.length > 0) {
                console.log("RCA: Using calculated_columns from pivot_table:", result.pivot_table.calculated_columns);
                return result.pivot_table.calculated_columns;
            }
            // Then check pivot_table_results (may have multiple pivot tables)
            if (result.pivot_table_results) {
                for (const stepId of Object.keys(result.pivot_table_results)) {
                    const stepResult = result.pivot_table_results[stepId];
                    if (stepResult?.config?.calculated_columns && stepResult.config.calculated_columns.length > 0) {
                        console.log(`RCA: Using calculated_columns from pivot_table_results[${stepId}]:`, stepResult.config.calculated_columns);
                        return stepResult.config.calculated_columns;
                    }
                }
            }
            console.log("RCA: No calculated_columns found in pivot_table or pivot_table_results");
            return [];
        };

        // Helper function to write calculated column descriptions above data
        const writeCalculatedColumnsDescription = (
            sheet: Excel.Worksheet,
            startRow: number,
            calculatedColumns: CalculatedColumn[] | undefined
        ): number => {
            // Returns the row number where data should start (after descriptions + empty row)
            if (!calculatedColumns || calculatedColumns.length === 0) {
                console.log("writeCalculatedColumnsDescription: No calculated columns to display");
                return startRow;
            }

            console.log("writeCalculatedColumnsDescription: Writing", calculatedColumns.length, "calculated columns");

            for (let i = 0; i < calculatedColumns.length; i++) {
                const col = calculatedColumns[i];
                const cell = sheet.getRangeByIndexes(startRow + i, 0, 1, 1);
                // Include formula if available for transparency
                const formulaText = col.formula ? ` | Formula: ${col.formula}` : "";
                const displayText = `${col.name}: ${col.description}${formulaText}`;
                console.log(`  - ${col.name}: formula=${col.formula || "(none)"}`);
                cell.values = [[displayText]];
                cell.format.font.italic = true;
                cell.format.font.color = "#475569";
            }

            // Return row after descriptions + 1 empty row
            return startRow + calculatedColumns.length + 1;
        };

        const handleCreatePivotTable = async (config?: PivotTableConfig) => {
            const pivotConfig = config || result.pivot_table;
            if (!pivotConfig || pivotConfig.error) {
                setWriteError("No pivot table configuration available");
                return;
            }

            setIsWritingData(true);
            setWriteError(null);
            setWriteSuccess(null);

            try {
                await Excel.run(async (context) => {
                    const workbook = context.workbook;

                    const { headers, rows: data } = sanitizeEnrichedTableData(
                        result.enriched_table.headers,
                        result.enriched_table.rows
                    );

                    // Create a hidden data sheet for pivot source
                    const dataSheetName = `_RCAData_${Date.now()}`;
                    const dataSheet = workbook.worksheets.add(dataSheetName);

                    // Write source data to hidden sheet
                    const headerRange = dataSheet.getRangeByIndexes(0, 0, 1, headers.length);
                    headerRange.values = [headers];

                    if (data.length > 0) {
                        const dataRange = dataSheet.getRangeByIndexes(1, 0, data.length, headers.length);
                        dataRange.values = data;
                    }

                    const fullRange = dataSheet.getRangeByIndexes(0, 0, data.length + 1, headers.length);
                    const sourceTable = dataSheet.tables.add(fullRange, true);
                    sourceTable.name = `RCASource_${Date.now()}`;

                    // Hide the data sheet
                    dataSheet.visibility = Excel.SheetVisibility.hidden;

                    await context.sync();

                    // Create pivot sheet (visible)
                    const pivotSheetName = `Analysis Pivot ${new Date().toLocaleTimeString().replace(/:/g, "-")}`;
                    const pivotSheet = workbook.worksheets.add(pivotSheetName);

                    // Write calculated column descriptions above pivot table
                    // Use pivotConfig's calculated_columns if available, otherwise fallback to result sources
                    const calcCols = pivotConfig.calculated_columns?.length
                        ? pivotConfig.calculated_columns
                        : getCalculatedColumnsFromResult();
                    const pivotStartRow = writeCalculatedColumnsDescription(
                        pivotSheet,
                        0,
                        calcCols
                    );

                    // Convert row number to Excel cell reference (e.g., 0 -> "A1", 3 -> "A4")
                    const pivotStartCell = `A${pivotStartRow + 1}`;

                    const pivotTable = pivotSheet.pivotTables.add(
                        `RCAPivot_${Date.now()}`,
                        sourceTable.getRange(),
                        pivotSheet.getRange(pivotStartCell)
                    );

                    const { rows, columns, values } = pivotConfig;

                    for (const rowField of rows || []) {
                        if (headers.includes(rowField)) {
                            pivotTable.rowHierarchies.add(pivotTable.hierarchies.getItem(rowField));
                        }
                    }

                    for (const colField of columns || []) {
                        if (headers.includes(colField)) {
                            pivotTable.columnHierarchies.add(pivotTable.hierarchies.getItem(colField));
                        }
                    }

                    for (const valueConfig of values || []) {
                        if (headers.includes(valueConfig.field)) {
                            const dataHierarchy = pivotTable.dataHierarchies.add(
                                pivotTable.hierarchies.getItem(valueConfig.field)
                            );

                            const aggregationMap: Record<string, Excel.AggregationFunction> = {
                                sum: Excel.AggregationFunction.sum,
                                average: Excel.AggregationFunction.average,
                                count: Excel.AggregationFunction.count,
                                min: Excel.AggregationFunction.min,
                                max: Excel.AggregationFunction.max
                            };

                            if (valueConfig.aggregation && aggregationMap[valueConfig.aggregation]) {
                                dataHierarchy.summarizeBy = aggregationMap[valueConfig.aggregation];
                            }
                        }
                    }

                    pivotSheet.activate();
                    await context.sync();

                    setWriteSuccess(`Created pivot table in "${pivotSheetName}"`);
                });
            } catch (err) {
                console.error("Failed to create pivot table:", err);
                setWriteError(err instanceof Error ? err.message : "Failed to create pivot table");
            } finally {
                setIsWritingData(false);
            }
        };

        const handleWriteGroupedGrid = async (gridData?: Array<Record<string, any>>, pivotConfig?: PivotTableConfig) => {
            const dataToWrite = gridData || result.grouped_grid;
            if (!dataToWrite || dataToWrite.length === 0) {
                setWriteError("No grouped grid data available");
                return;
            }

            setIsWritingData(true);
            setWriteError(null);
            setWriteSuccess(null);

            try {
                await Excel.run(async (context) => {
                    const workbook = context.workbook;

                    const headers = Object.keys(dataToWrite[0]).filter(k => k !== "_level");
                    const data = dataToWrite.map(obj => headers.map(h => obj[h] ?? ""));

                    const sheetName = `RCA Grouped ${new Date().toLocaleTimeString().replace(/:/g, "-")}`;
                    const newSheet = workbook.worksheets.add(sheetName);

                    // Write calculated column descriptions above grouped grid
                    // First try the passed pivotConfig, then fallback to result sources
                    const calcCols = pivotConfig?.calculated_columns?.length
                        ? pivotConfig.calculated_columns
                        : getCalculatedColumnsFromResult();
                    const dataStartRow = writeCalculatedColumnsDescription(
                        newSheet,
                        0,
                        calcCols
                    );

                    const headerRange = newSheet.getRangeByIndexes(dataStartRow, 0, 1, headers.length);
                    headerRange.values = [headers];
                    headerRange.format.font.bold = true;
                    headerRange.format.fill.color = "#068E75";
                    headerRange.format.font.color = "#FFFFFF";

                    if (data.length > 0) {
                        const dataRange = newSheet.getRangeByIndexes(dataStartRow + 1, 0, data.length, headers.length);
                        dataRange.values = data;
                    }

                    const fullRange = newSheet.getRangeByIndexes(dataStartRow, 0, data.length + 1, headers.length);
                    fullRange.format.autofitColumns();

                    const tableRange = newSheet.getRangeByIndexes(dataStartRow, 0, data.length + 1, headers.length);
                    const table = newSheet.tables.add(tableRange, true);
                    table.name = `RCAGrouped_${Date.now()}`;
                    table.style = "TableStyleMedium2";

                    newSheet.activate();
                    await context.sync();

                    setWriteSuccess(`Grouped grid written to sheet "${sheetName}" with ${data.length} rows`);
                });
            } catch (err) {
                console.error("Failed to write grouped grid:", err);
                setWriteError(err instanceof Error ? err.message : "Failed to write grouped grid to Excel");
            } finally {
                setIsWritingData(false);
            }
        };




        // Main: Create RCA Analysis report sheet
        const createRCAAnalysisSheet = async (): Promise<void> => {
            setIsWritingData(true);
            setWriteError(null);
            setWriteSuccess(null);

            try {
                await Excel.run(async (context) => {
                    const timestamp = new Date().toLocaleTimeString().replace(/:/g, "-");
                    const sheetName = `Analysis Report ${timestamp}`;

                    if (result.excel_styled_report) {
                        // v6.0.0: Use JESS renderer for structured Excel output
                        console.log("RCA: Using JESS renderer for analysis sheet");
                        const jess = result.excel_styled_report as Record<string, any>;
                        console.log("=== RCA JESS Report Details ===");
                        console.log("executive_summary:", jess.executive_summary);
                        console.log("sections count:", jess.sections?.length);
                        jess.sections?.forEach((sec: any, i: number) => {
                            console.log(
                                `  Section ${i + 1}: "${sec.title}" [preset=${sec.style_preset || "default"}, ` +
                                `metrics=${sec.metrics?.length || 0}, list_items=${sec.list_items?.length || 0}, ` +
                                `paragraphs=${sec.paragraphs?.length || 0}, has_table=${!!sec.table}]`
                            );
                            if (sec.paragraphs) {
                                sec.paragraphs.forEach((p: any, j: number) => {
                                    console.log(`    Paragraph ${j + 1}: text=${p.text ? p.text.substring(0, 80) + "..." : "UNDEFINED"}, style=${p.style || "normal"}`);
                                });
                            }
                        });
                        console.log("top_metrics count:", jess.top_metrics?.length);
                        jess.top_metrics?.forEach((m: any, i: number) => {
                            console.log(`  Metric ${i + 1}: ${m.label} = ${m.value} [badge=${m.badge?.text || "none"}]`);
                        });
                        console.log("=== Full RCA JESS JSON ===");
                        console.log(JSON.stringify(jess, null, 2));
                        console.log("=== End RCA JESS JSON ===");
                        const jessReport = result.excel_styled_report as unknown as ExcelStyledReport;
                        const sheet = await renderExcelStyledReport(
                            context,
                            jessReport,
                            sheetName,
                            `Question: ${userQuestion}`
                        );
                        sheet.activate();
                        await context.sync();
                    } else if (result.html_report) {
                        // Convert HTML to JESS and render with structured renderer
                        console.log("RCA: Converting HTML to JESS for analysis sheet");
                        const jessReport = convertHtmlToJess(
                            result.html_report,
                            result.summary || ""
                        );
                        const sheet = await renderExcelStyledReport(
                            context,
                            jessReport,
                            sheetName,
                            `Question: ${userQuestion}`
                        );
                        sheet.activate();
                        await context.sync();
                    } else {
                        // Fallback: build minimal JESS report from summary
                        console.log("RCA: Building minimal JESS from summary");
                        const minimalJess: ExcelStyledReport = {
                            executive_summary: result.summary || "Analysis complete.",
                            top_metrics: [],
                            sections: [],
                        };
                        const sheet = await renderExcelStyledReport(
                            context,
                            minimalJess,
                            sheetName,
                            `Question: ${userQuestion}`
                        );
                        sheet.activate();
                        await context.sync();
                    }

                    setWriteSuccess(`Analysis report written to sheet "${sheetName}"`);
                });
            } catch (err) {
                console.error("Failed to create RCA analysis sheet:", err);
                setWriteError(err instanceof Error ? err.message : "Failed to create analysis report");
            } finally {
                setIsWritingData(false);
            }
        };

        const handleWriteSmartMatchTables = async () => {
            if (!result.smart_match_tables) {
                setWriteError("No smart match results available");
                return;
            }

            try {
                await Excel.run(async (context) => {
                    const tableNames = Object.keys(result.smart_match_tables!);
                    let lastSheet: Excel.Worksheet | null = null;

                    for (const tableName of tableNames) {
                        const smTable = result.smart_match_tables![tableName];

                        // Filter out column_scores from headers and rows
                        const columnScoresIdx = smTable.headers.indexOf("column_scores");
                        const filteredHeaders = smTable.headers.filter((_, i) => i !== columnScoresIdx);
                        const filteredRows = smTable.rows.map(row =>
                            row.filter((_, i) => i !== columnScoresIdx)
                                .map(val => {
                                    if (val === null || val === undefined) return "";
                                    if (typeof val === "object") return JSON.stringify(val);
                                    return val;
                                })
                        );

                        const timestamp = new Date().toLocaleTimeString().replace(/:/g, "-");
                        const sheetName = `Analysis SM ${tableName} ${timestamp}`.substring(0, SHEET_NAME_MAX_LENGTH); // Excel 31-char limit

                        // Delete existing sheet with same name if it exists
                        const existing = context.workbook.worksheets.getItemOrNullObject(sheetName);
                        await context.sync();
                        if (!existing.isNullObject) {
                            existing.delete();
                            await context.sync();
                        }

                        const newSheet = context.workbook.worksheets.add(sheetName);

                        // Headers - bold, teal background, white text
                        const headerRange = newSheet.getRangeByIndexes(0, 0, 1, filteredHeaders.length);
                        headerRange.values = [filteredHeaders];
                        headerRange.format.font.bold = true;
                        headerRange.format.fill.color = "#068E75";
                        headerRange.format.font.color = "#FFFFFF";

                        // Data rows
                        if (filteredRows.length > 0) {
                            const dataRange = newSheet.getRangeByIndexes(1, 0, filteredRows.length, filteredHeaders.length);
                            dataRange.values = filteredRows;
                        }

                        // Create Excel Table with auto-filter
                        const tableRange = newSheet.getRangeByIndexes(0, 0, filteredRows.length + 1, filteredHeaders.length);
                        const table = newSheet.tables.add(tableRange, true);
                        table.name = `RCA_SM_${tableName.replace(/[^a-zA-Z0-9]/g, '_')}_${Date.now()}`;
                        table.style = "TableStyleMedium2";

                        // Auto-fit columns
                        newSheet.getUsedRange().format.autofitColumns();
                        lastSheet = newSheet;
                    }

                    // Activate the last created sheet
                    if (lastSheet) {
                        lastSheet.activate();
                    }
                    await context.sync();
                });

                const count = Object.keys(result.smart_match_tables!).length;
                setWriteSuccess(`Smart match results written to ${count} sheet(s)`);
            } catch (err) {
                console.error("Failed to write smart match tables:", err);
                throw err;
            }
        };

        const handleExecuteStep = async (stepId: string) => {
            setExecutingStep(stepId);
            setWriteError(null);
            setWriteSuccess(null);

            try {
                // Extract base type to handle indexed step IDs (e.g., "smart_column_2")
                const baseType = getBaseStepType(stepId);
                switch (baseType) {
                    case "smart_column":
                        // Write generated columns to Excel if available
                        if (result.generated_columns && result.generated_columns.length > 0) {
                            await handleWriteToExcel();
                            setStepResults(prev => ({ ...prev, [stepId]: "success" }));
                            setWriteSuccess(`Smart columns generated successfully`);
                        } else {
                            setStepResults(prev => ({ ...prev, [stepId]: "success" }));
                            setWriteSuccess(`No additional smart columns were generated for this analysis`);
                        }
                        break;

                    case "smart_match":
                        if (result.smart_match_tables && Object.keys(result.smart_match_tables).length > 0) {
                            await handleWriteSmartMatchTables();
                            setStepResults(prev => ({ ...prev, [stepId]: "success" }));
                            setWriteSuccess("Smart match results written to Excel");
                        } else {
                            setStepResults(prev => ({ ...prev, [stepId]: "success" }));
                            setWriteSuccess("No smart match data was produced for this analysis");
                        }
                        break;

                    case "pivot_table":
                        // Get step-specific pivot data and grid type (supports multiple pivot tables)
                        const { config: pivotConfig, groupedGrid } = getPivotTableDataForStep(stepId);
                        const stepGridType = getGridTypeForStep(stepId);

                        if (stepGridType === "grouped") {
                            if (groupedGrid && groupedGrid.length > 0) {
                                await handleWriteGroupedGrid(groupedGrid, pivotConfig || undefined);
                                setStepResults(prev => ({ ...prev, [stepId]: "success" }));
                            } else {
                                setStepResults(prev => ({ ...prev, [stepId]: "error" }));
                                setWriteError("No grouped grid data available for this step");
                            }
                        } else if (pivotConfig && !pivotConfig.error) {
                            await handleCreatePivotTable(pivotConfig);
                            setStepResults(prev => ({ ...prev, [stepId]: "success" }));
                        } else {
                            setStepResults(prev => ({ ...prev, [stepId]: "error" }));
                            setWriteError("Pivot table configuration not available for this step");
                        }
                        break;

                    case "rca_analysis":
                        // Write the analysis report to Excel
                        await createRCAAnalysisSheet();
                        setStepResults(prev => ({ ...prev, [stepId]: "success" }));
                        break;

                    default:
                        setStepResults(prev => ({ ...prev, [stepId]: "error" }));
                        setWriteError(`Unknown step type: ${baseType}`);
                }
            } catch (err) {
                console.error(`Failed to execute step ${stepId}:`, err);
                setStepResults(prev => ({ ...prev, [stepId]: "error" }));
                setWriteError(err instanceof Error ? err.message : `Failed to execute ${stepId}`);
            } finally {
                setExecutingStep(null);
            }
        };

        // Get enabled steps from plan or result
        const enabledSteps = useMemo(() => {
            const planToUse = plan || result.plan;
            if (planToUse && planToUse.steps) {
                return planToUse.steps.filter(step => step.enabled);
            }
            return [];
        }, [plan, result.plan]);

        // Initialize selected steps with all enabled steps (selected by default)
        useEffect(() => {
            if (enabledSteps.length > 0) {
                setSelectedSteps(new Set(enabledSteps.map(step => step.step_id)));
            }
        }, [enabledSteps]);

        // Toggle step selection
        const toggleStepSelection = (stepId: string) => {
            setSelectedSteps(prev => {
                const newSet = new Set(prev);
                if (newSet.has(stepId)) {
                    newSet.delete(stepId);
                } else {
                    newSet.add(stepId);
                }
                return newSet;
            });
        };

        // Render all selected steps
        const handleRenderSelected = async () => {
            if (selectedSteps.size === 0) {
                setWriteError("Please select at least one step to render");
                return;
            }

            setIsRenderingAll(true);
            setWriteError(null);
            setWriteSuccess(null);

            const stepsToRender = enabledSteps.filter(step => selectedSteps.has(step.step_id));
            const results: string[] = [];

            try {
                for (const step of stepsToRender) {
                    setExecutingStep(step.step_id);
                    // Extract base type to handle indexed step IDs (e.g., "smart_column_2")
                    const baseType = getBaseStepType(step.step_id);

                    switch (baseType) {
                        case "smart_column":
                            if (result.generated_columns && result.generated_columns.length > 0) {
                                await handleWriteToExcel();
                                setStepResults(prev => ({ ...prev, [step.step_id]: "success" }));
                                results.push("Smart columns");
                            } else {
                                setStepResults(prev => ({ ...prev, [step.step_id]: "success" }));
                                results.push("Smart columns (no additional columns)");
                            }
                            break;

                        case "smart_match":
                            if (result.smart_match_tables && Object.keys(result.smart_match_tables).length > 0) {
                                await handleWriteSmartMatchTables();
                                setStepResults(prev => ({ ...prev, [step.step_id]: "success" }));
                                results.push("Smart match");
                            } else {
                                setStepResults(prev => ({ ...prev, [step.step_id]: "success" }));
                                results.push("Smart match (no data)");
                            }
                            break;

                        case "pivot_table":
                            // Get step-specific pivot data and grid type (supports multiple pivot tables)
                            const { config: stepPivotConfig, groupedGrid: stepGroupedGrid } = getPivotTableDataForStep(step.step_id);
                            const renderStepGridType = getGridTypeForStep(step.step_id);

                            if (renderStepGridType === "grouped") {
                                if (stepGroupedGrid && stepGroupedGrid.length > 0) {
                                    await handleWriteGroupedGrid(stepGroupedGrid, stepPivotConfig || undefined);
                                    setStepResults(prev => ({ ...prev, [step.step_id]: "success" }));
                                    results.push("Grouped grid");
                                } else {
                                    setStepResults(prev => ({ ...prev, [step.step_id]: "error" }));
                                }
                            } else if (stepPivotConfig && !stepPivotConfig.error) {
                                await handleCreatePivotTable(stepPivotConfig);
                                setStepResults(prev => ({ ...prev, [step.step_id]: "success" }));
                                results.push("Pivot table");
                            } else {
                                setStepResults(prev => ({ ...prev, [step.step_id]: "error" }));
                            }
                            break;

                        case "rca_analysis":
                            await createRCAAnalysisSheet();
                            setStepResults(prev => ({ ...prev, [step.step_id]: "success" }));
                            results.push("Analysis report");
                            break;
                    }
                }

                if (results.length > 0) {
                    setWriteSuccess(`Successfully rendered: ${results.join(", ")}`);
                }
            } catch (err) {
                console.error("Failed to render selected steps:", err);
                setWriteError(err instanceof Error ? err.message : "Failed to render selected steps");
            } finally {
                setExecutingStep(null);
                setIsRenderingAll(false);
            }
        };

        return (
            <>
                <style>
                    {`
                    .bg-gradient-custom {
                        background: linear-gradient(180deg, #D3E8D9 0%, #EEEEEE 79.45%);
                    }
                    .hide-scrollbar::-webkit-scrollbar {
                        display: none;
                    }
                    .hide-scrollbar {
                        -ms-overflow-style: none;
                        scrollbar-width: none;
                    }
                    .stat-card {
                        background: rgba(255, 255, 255, 0.6);
                        border: 1px solid rgba(255, 255, 255, 0.8);
                        border-radius: 8px;
                        padding: 12px;
                        text-align: center;
                    }
                    .stat-value {
                        font-size: 20px;
                        font-weight: 600;
                        color: #111827;
                    }
                    .stat-label {
                        font-size: 11px;
                        color: #6B7280;
                        margin-top: 2px;
                    }
                    /* Enhanced Report Section Styles */
                    .report-section {
                        background: linear-gradient(180deg, rgba(255,255,255,0.75), rgba(255,255,255,0.55));
                        backdrop-filter: blur(12px);
                        border: 1px solid rgba(255,255,255,0.7);
                        border-radius: 14px;
                        margin-bottom: 12px;
                        overflow: hidden;
                        box-shadow: 0 4px 12px rgba(0,0,0,0.06);
                        transition: all 0.2s ease;
                    }
                    .report-section:hover {
                        box-shadow: 0 6px 16px rgba(0,0,0,0.08);
                    }
                    .report-section-header {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        padding: 14px 16px;
                        cursor: pointer;
                        background: linear-gradient(180deg, rgba(6,142,117,0.06), rgba(6,142,117,0.02));
                        border-bottom: 1px solid rgba(6,142,117,0.1);
                        transition: background 0.2s ease;
                    }
                    .report-section-header:hover {
                        background: linear-gradient(180deg, rgba(6,142,117,0.1), rgba(6,142,117,0.04));
                    }
                    .report-section-header.collapsed {
                        border-bottom: none;
                    }
                    .report-section-title {
                        display: flex;
                        align-items: center;
                        gap: 10px;
                        font-size: 14px;
                        font-weight: 600;
                        color: #0F172A;
                        margin: 0;
                    }
                    .report-section-content {
                        padding: 16px;
                        animation: fadeIn 0.2s ease;
                    }
                    @keyframes fadeIn {
                        from { opacity: 0; transform: translateY(-4px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                    .report-section-content p {
                        color: #475569;
                        line-height: 1.7;
                        font-size: 13px;
                        margin: 0 0 12px 0;
                    }
                    .report-section-content p:last-child {
                        margin-bottom: 0;
                    }
                    .report-section-content ul, .report-section-content ol {
                        color: #475569;
                        padding-left: 0;
                        margin: 12px 0;
                        font-size: 13px;
                        list-style: none;
                    }
                    .report-section-content li {
                        position: relative;
                        padding: 10px 12px 10px 28px;
                        margin-bottom: 8px;
                        background: rgba(255,255,255,0.6);
                        border: 1px solid rgba(6,142,117,0.12);
                        border-radius: 8px;
                        line-height: 1.5;
                        transition: all 0.15s ease;
                    }
                    .report-section-content li:hover {
                        background: rgba(255,255,255,0.8);
                        border-color: rgba(6,142,117,0.2);
                    }
                    .report-section-content li::before {
                        content: "";
                        position: absolute;
                        left: 12px;
                        top: 14px;
                        width: 6px;
                        height: 6px;
                        background: #068E75;
                        border-radius: 50%;
                    }
                    .report-section-content li:last-child {
                        margin-bottom: 0;
                    }
                    .report-section-content strong {
                        color: #0F172A;
                        font-weight: 600;
                    }
                    .report-section-content code {
                        background: rgba(6,142,117,0.1);
                        color: #065F46;
                        padding: 2px 8px;
                        border-radius: 4px;
                        font-size: 12px;
                        font-family: 'SF Mono', Monaco, monospace;
                    }
                    .report-section-content table {
                        width: 100%;
                        border-collapse: collapse;
                        margin: 12px 0;
                        font-size: 12px;
                    }
                    .report-section-content th {
                        background: rgba(6,142,117,0.1);
                        color: #065F46;
                        padding: 10px 12px;
                        text-align: left;
                        font-weight: 600;
                        border-bottom: 2px solid rgba(6,142,117,0.2);
                    }
                    .report-section-content td {
                        padding: 10px 12px;
                        border-bottom: 1px solid rgba(0,0,0,0.06);
                        color: #475569;
                    }
                    .report-section-content tr:hover td {
                        background: rgba(6,142,117,0.03);
                    }
                    .report-controls {
                        display: flex;
                        justify-content: flex-end;
                        gap: 8px;
                        margin-bottom: 12px;
                    }
                    .report-control-btn {
                        padding: 6px 12px;
                        font-size: 11px;
                        font-weight: 500;
                        background: rgba(255,255,255,0.6);
                        border: 1px solid rgba(6,142,117,0.2);
                        border-radius: 6px;
                        color: #065F46;
                        cursor: pointer;
                        transition: all 0.15s ease;
                    }
                    .report-control-btn:hover {
                        background: rgba(6,142,117,0.1);
                        border-color: rgba(6,142,117,0.3);
                    }
                    .report-empty-state {
                        text-align: center;
                        padding: 48px 24px;
                        background: linear-gradient(180deg, rgba(255,255,255,0.6), rgba(255,255,255,0.4));
                        border-radius: 14px;
                        border: 1px solid rgba(255,255,255,0.6);
                    }
                    .report-empty-icon {
                        width: 48px;
                        height: 48px;
                        margin: 0 auto 16px;
                        background: rgba(6,142,117,0.1);
                        border-radius: 50%;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    }
                    /* Legacy html-report fallback styles */
                    .html-report h1, .html-report h2, .html-report h3 {
                        color: #111827;
                        margin-top: 1em;
                        margin-bottom: 0.5em;
                    }
                    .html-report h1 { font-size: 1.25em; }
                    .html-report h2 { font-size: 1.1em; }
                    .html-report h3 { font-size: 1em; }
                    .html-report p { color: #374151; line-height: 1.6; font-size: 13px; }
                    .html-report ul, .html-report ol {
                        color: #374151;
                        padding-left: 1.5em;
                        margin: 0.5em 0;
                        font-size: 13px;
                    }
                    .html-report li { margin: 0.25em 0; }
                    .html-report strong { color: #111827; }
                    .html-report code {
                        background: rgba(0,0,0,0.05);
                        padding: 2px 6px;
                        border-radius: 4px;
                        font-size: 0.9em;
                    }
                    .column-chip {
                        display: inline-flex;
                        align-items: center;
                        gap: 6px;
                        padding: 4px 10px;
                        background: rgba(6, 142, 117, 0.1);
                        border: 1px solid rgba(6, 142, 117, 0.2);
                        border-radius: 12px;
                        font-size: 11px;
                        color: #065F46;
                    }
                    /* Plan steps styles */
                    @keyframes spin {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                    }
                    .plan-step-card {
                        background: linear-gradient(180deg, rgba(255,255,255,0.75), rgba(255,255,255,0.55));
                        backdrop-filter: blur(12px);
                        border: 1px solid rgba(255,255,255,0.7);
                        border-radius: 10px;
                        padding: 12px 14px;
                        margin-bottom: 10px;
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        gap: 12px;
                        transition: all 0.2s ease;
                    }
                    .plan-step-card:hover {
                        box-shadow: 0 4px 12px rgba(0,0,0,0.06);
                    }
                    .plan-step-card.executed {
                        border-color: rgba(6, 142, 117, 0.3);
                        background: linear-gradient(180deg, rgba(6,142,117,0.08), rgba(6,142,117,0.04));
                    }
                    .plan-step-card.error {
                        border-color: rgba(239, 68, 68, 0.3);
                        background: linear-gradient(180deg, rgba(239,68,68,0.08), rgba(239,68,68,0.04));
                    }
                    .plan-step-info {
                        display: flex;
                        align-items: center;
                        gap: 10px;
                        flex: 1;
                        min-width: 0;
                    }
                    .plan-step-number {
                        width: 24px;
                        height: 24px;
                        border-radius: 50%;
                        background: linear-gradient(135deg, #068E75 0%, #057A63 100%);
                        color: white;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 11px;
                        font-weight: 600;
                        flex-shrink: 0;
                    }
                    .plan-step-title {
                        font-size: 13px;
                        font-weight: 500;
                        color: #0F172A;
                        margin: 0;
                    }
                    .plan-step-execute-btn {
                        padding: 6px 12px;
                        font-size: 11px;
                        font-weight: 500;
                        background: #068E75;
                        border: none;
                        border-radius: 6px;
                        color: white;
                        cursor: pointer;
                        transition: all 0.15s ease;
                        display: flex;
                        align-items: center;
                        gap: 4px;
                        flex-shrink: 0;
                    }
                    .plan-step-execute-btn:hover:not(:disabled) {
                        background: #057A63;
                    }
                    .plan-step-execute-btn:disabled {
                        background: #9CA3AF;
                        cursor: not-allowed;
                    }
                    .plan-step-status {
                        font-size: 11px;
                        font-weight: 500;
                        padding: 4px 10px;
                        border-radius: 12px;
                        flex-shrink: 0;
                    }
                    .plan-step-status.success {
                        background: rgba(6, 142, 117, 0.1);
                        color: #065F46;
                    }
                    .plan-step-status.error {
                        background: rgba(239, 68, 68, 0.1);
                        color: #DC2626;
                    }
                    .plan-step-wrapper {
                        margin-bottom: 12px;
                    }
                    .plan-step-wrapper:last-child {
                        margin-bottom: 0;
                    }
                    .plan-step-wrapper .plan-step-card {
                        margin-bottom: 0;
                    }
                    .plan-step-reason {
                        font-size: 12px;
                        color: #64748B;
                        line-height: 1.5;
                        margin: 8px 0 0 44px;
                        padding: 8px 12px;
                        background: rgba(6, 142, 117, 0.04);
                        border-left: 2px solid rgba(6, 142, 117, 0.3);
                        border-radius: 0 6px 6px 0;
                    }
                    .plan-step-card.executing {
                        border-color: rgba(6, 142, 117, 0.5);
                        background: linear-gradient(180deg, rgba(6,142,117,0.12), rgba(6,142,117,0.06));
                    }
                    /* Checkbox styles */
                    .step-checkbox-container {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        cursor: pointer;
                        flex-shrink: 0;
                    }
                    .step-checkbox {
                        position: absolute;
                        opacity: 0;
                        cursor: pointer;
                        height: 0;
                        width: 0;
                    }
                    .step-checkbox-custom {
                        width: 18px;
                        height: 18px;
                        border: 2px solid #D1D5DB;
                        border-radius: 4px;
                        background: white;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        transition: all 0.2s ease;
                    }
                    .step-checkbox:checked + .step-checkbox-custom {
                        background: #068E75;
                        border-color: #068E75;
                    }
                    .step-checkbox:checked + .step-checkbox-custom::after {
                        content: "";
                        width: 5px;
                        height: 9px;
                        border: solid white;
                        border-width: 0 2px 2px 0;
                        transform: rotate(45deg);
                        margin-bottom: 2px;
                    }
                    .step-checkbox:disabled + .step-checkbox-custom {
                        opacity: 0.5;
                        cursor: not-allowed;
                    }
                    .step-checkbox-container:hover .step-checkbox:not(:disabled) + .step-checkbox-custom {
                        border-color: #068E75;
                    }
                    /* Render Selected Button */
                                    .render-selected-btn {
                                        width: 100%;
                                        margin-top: 16px;
                                        padding: 12px 16px;
                                        font-size: 13px;
                                        font-weight: 600;
                                        background: linear-gradient(135deg, #068E75 0%, #057A63 100%);
                                        border: none;
                                        border-radius: 10px;
                                        color: white;
                                        cursor: pointer;
                                        transition: all 0.2s ease;
                                        display: flex;
                                        align-items: center;
                                        justify-content: center;
                                        gap: 8px;
                                        box-shadow: 0 2px 8px rgba(6, 142, 117, 0.3);
                                    }
                                    .render-selected-btn:hover:not(:disabled) {
                                        background: linear-gradient(135deg, #057A63 0%, #046B56 100%);
                                        transform: translateY(-1px);
                                        box-shadow: 0 4px 12px rgba(6, 142, 117, 0.4);
                                    }
                                    .render-selected-btn:disabled {
                                        background: #9CA3AF;
                                        cursor: not-allowed;
                                        box-shadow: none;
                                    }

                                    /* ========================================
                                       DATA TAB STYLES - Figma Matching Design
                                       ======================================== */
                                    
                                    .data-tab-container {
                                        display: flex;
                                        flex-direction: column;
                                        gap: 12px;
                                    }

                                    /* Parent Transparent Card Container */
                                    .data-section-card {
                                        background: rgba(255, 255, 255, 0.5);
                                        border-radius: 16px;
                                        padding: 16px;
                                        overflow: hidden;
                                    }

                                    /* Section Header - Title and Badge */
                                    .data-section-header {
                                        display: flex;
                                        align-items: center;
                                        justify-content: space-between;
                                        margin-bottom: 16px;
                                    }

                                    .data-section-title {
                                        display: flex;
                                        align-items: center;
                                        gap: 0;
                                        font-size: 14px;
                                        font-weight: 600;
                                        color: #1F2937;
                                    }

                                    .data-section-badge {
                                        font-size: 11px;
                                        font-weight: 500;
                                        color: #068E75;
                                        background: rgba(6, 142, 117, 0.1);
                                        padding: 4px 10px;
                                        border-radius: 12px;
                                    }

                                    /* Steps List Container */
                                    .data-steps-list {
                                        display: flex;
                                        flex-direction: column;
                                        gap: 12px;
                                        padding: 0;
                                    }

                                    .data-step-item {
                                        display: flex;
                                        flex-direction: column;
                                    }

                                    /* Individual Step Card - White with subtle styling */
                                    .data-step-card {
                                        background: rgba(255, 255, 255, 0.9);
                                        border: 1px solid rgba(0, 0, 0, 0.06);
                                        border-radius: 14px;
                                        padding: 14px 16px;
                                        display: flex;
                                        align-items: flex-start;
                                        gap: 14px;
                                        transition: all 0.15s ease;
                                    }

                                    .data-step-card:hover {
                                        background: rgba(255, 255, 255, 0.95);
                                    }

                                    .data-step-card.executed {
                                        border-color: rgba(6, 142, 117, 0.2);
                                        background: rgba(255, 255, 255, 0.95);
                                    }

                                    .data-step-card.error {
                                        border-color: rgba(239, 68, 68, 0.2);
                                        background: rgba(255, 255, 255, 0.95);
                                    }

                                    .data-step-card.executing {
                                        border-color: rgba(6, 142, 117, 0.3);
                                        background: rgba(255, 255, 255, 0.98);
                                    }

                                    /* Blue Checkbox Styles - Matching Figma */
                                    .data-checkbox-wrapper {
                                        position: relative;
                                        display: flex;
                                        align-items: center;
                                        justify-content: center;
                                        cursor: pointer;
                                        flex-shrink: 0;
                                        margin-top: 2px;
                                    }

                                    .data-checkbox-input {
                                        position: absolute;
                                        opacity: 0;
                                        cursor: pointer;
                                        width: 0;
                                        height: 0;
                                    }

                                    .data-checkbox-box {
                                        width: 22px;
                                        height: 22px;
                                        border: 2px solid #3B82F6;
                                        border-radius: 6px;
                                        background: white;
                                        display: flex;
                                        align-items: center;
                                        justify-content: center;
                                        transition: all 0.15s ease;
                                    }

                                    .data-checkbox-check {
                                        width: 12px;
                                        height: 12px;
                                        opacity: 0;
                                        transition: opacity 0.15s ease;
                                    }

                                    .data-checkbox-input:checked + .data-checkbox-box {
                                        background: #3B82F6;
                                        border-color: #3B82F6;
                                    }

                                    .data-checkbox-input:checked + .data-checkbox-box .data-checkbox-check {
                                        opacity: 1;
                                    }

                                    .data-checkbox-wrapper:hover .data-checkbox-box {
                                        border-color: #2563EB;
                                    }

                                    .data-checkbox-input:disabled + .data-checkbox-box {
                                        opacity: 0.5;
                                        cursor: not-allowed;
                                    }

                                    /* Step Main Container */
                                    .data-step-main {
                                        flex: 1;
                                        min-width: 0;
                                        display: flex;
                                        flex-direction: column;
                                        gap: 0;
                                    }

                                    .data-step-header-row {
                                        display: flex;
                                        align-items: center;
                                        gap: 8px;
                                    }

                                    /* Hide Step Icon - not in Figma design */
                                    .data-step-icon {
                                        display: none;
                                    }

                                    /* Step Title */
                                    .data-step-title {
                                        font-size: 14px;
                                        font-weight: 600;
                                        color: #1F2937;
                                        margin: 0;
                                        line-height: 1.4;
                                    }

                                    /* Description Box - Light teal/cyan background matching Figma */
                                    .data-step-description-box {
                                        margin-top: 10px;
                                        padding: 12px 14px;
                                        background: rgba(6, 182, 212, 0.08);
                                        border-left: 3px solid rgba(6, 182, 212, 0.5);
                                        border-radius: 0 8px 8px 0;
                                    }

                                    .data-step-description-text {
                                        font-size: 12px;
                                        color: #64748B;
                                        line-height: 1.6;
                                        margin: 0;
                                    }

                                    /* Spinner */
                                    .data-step-spinner {
                                        width: 16px;
                                        height: 16px;
                                        border: 2px solid rgba(6, 142, 117, 0.2);
                                        border-top-color: #068E75;
                                        border-radius: 50%;
                                        animation: spin 0.8s linear infinite;
                                        flex-shrink: 0;
                                        margin-top: 2px;
                                    }

                                    /* Grid Type Custom Dropdown - Matching sheet selection style */
                                    .data-step-grid-select {
                                        display: flex;
                                        align-items: center;
                                        gap: 10px;
                                        margin-top: 10px;
                                    }
                                    .data-step-grid-select .dropdown-label {
                                        font-size: 12px;
                                        font-weight: 500;
                                        color: #475569;
                                        white-space: nowrap;
                                    }
                                    .data-step-grid-select .dropdown-container {
                                        flex: 1;
                                        position: relative;
                                    }
                                    .data-step-grid-select .dropdown-button {
                                        width: 100%;
                                        padding: 8px 12px;
                                        border: 1px solid #E5E7EB;
                                        border-radius: 8px;
                                        background: rgba(255, 255, 255, 0.9);
                                        font-size: 12px;
                                        color: #1F2937;
                                        text-align: left;
                                        cursor: pointer;
                                        display: flex;
                                        justify-content: space-between;
                                        align-items: center;
                                        transition: all 0.15s ease;
                                    }
                                    .data-step-grid-select .dropdown-button:hover:not(:disabled) {
                                        border-color: #D1D5DB;
                                        background: white;
                                    }
                                    .data-step-grid-select .dropdown-button:disabled {
                                        opacity: 0.5;
                                        cursor: not-allowed;
                                    }
                                    .data-step-grid-select .dropdown-button .dropdown-text {
                                        flex: 1;
                                        overflow: hidden;
                                        text-overflow: ellipsis;
                                        white-space: nowrap;
                                    }
                                    .data-step-grid-select .dropdown-arrow {
                                        flex-shrink: 0;
                                        transition: transform 0.2s ease;
                                    }
                                    .data-step-grid-select .dropdown-arrow.open {
                                        transform: rotate(180deg);
                                    }
                                    .data-step-grid-select .dropdown-menu {
                                        position: absolute;
                                        top: 100%;
                                        left: 0;
                                        right: 0;
                                        margin-top: 4px;
                                        background: white;
                                        border: 1px solid #E5E7EB;
                                        border-radius: 8px;
                                        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                                        z-index: 50;
                                        overflow: hidden;
                                        animation: dropdownFadeIn 0.15s ease;
                                    }
                                    @keyframes dropdownFadeIn {
                                        from {
                                            opacity: 0;
                                            transform: translateY(-4px);
                                        }
                                        to {
                                            opacity: 1;
                                            transform: translateY(0);
                                        }
                                    }
                                    .data-step-grid-select .dropdown-option {
                                        width: 100%;
                                        padding: 10px 12px;
                                        border: none;
                                        background: transparent;
                                        font-size: 12px;
                                        color: #374151;
                                        text-align: left;
                                        cursor: pointer;
                                        transition: background 0.1s ease;
                                    }
                                    .data-step-grid-select .dropdown-option:hover {
                                        background: #F3F4F6;
                                    }
                                    .data-step-grid-select .dropdown-option.selected {
                                        background: rgba(6, 142, 117, 0.08);
                                        color: #068E75;
                                        font-weight: 500;
                                    }
                                    .data-step-grid-select .dropdown-option:not(:last-child) {
                                        border-bottom: 1px solid #F3F4F6;
                                    }

                                    /* Render Button */
                                    .data-render-btn-wrapper {
                                        padding: 14px 0 0 0;
                                    }

                                    .data-render-btn {
                                        width: 100%;
                                        padding: 14px 20px;
                                        font-size: 14px;
                                        font-weight: 600;
                                        background: #068E75;
                                        border: none;
                                        border-radius: 12px;
                                        color: white;
                                        cursor: pointer;
                                        transition: all 0.2s ease;
                                        display: flex;
                                        align-items: center;
                                        justify-content: center;
                                        gap: 8px;
                                        box-shadow: 0 2px 8px rgba(6, 142, 117, 0.25);
                                    }

                                    .data-render-btn:hover:not(:disabled) {
                                        background: #057A63;
                                        box-shadow: 0 4px 12px rgba(6, 142, 117, 0.35);
                                    }

                                    .data-render-btn:active:not(:disabled) {
                                        transform: scale(0.98);
                                    }

                                    .data-render-btn:disabled {
                                        background: #9CA3AF;
                                        cursor: not-allowed;
                                        box-shadow: none;
                                    }

                                    .data-btn-spinner {
                                        width: 16px;
                                        height: 16px;
                                        border: 2px solid rgba(255, 255, 255, 0.3);
                                        border-top-color: white;
                                        border-radius: 50%;
                                        animation: spin 0.8s linear infinite;
                                    }

                                    /* Messages */
                                    .data-message {
                                        display: flex;
                                        align-items: center;
                                        gap: 8px;
                                        padding: 12px 14px;
                                        border-radius: 10px;
                                        font-size: 12px;
                                        font-weight: 500;
                                    }

                                    .data-message-icon {
                                        width: 16px;
                                        height: 16px;
                                        flex-shrink: 0;
                                    }

                                    .data-message-success {
                                        background: #D1FAE5;
                                        color: #065F46;
                                        border: 1px solid #A7F3D0;
                                    }

                                    .data-message-error {
                                        background: #FEF3C7;
                                        color: #92400E;
                                        border: 1px solid #FDE68A;
                                    }

                    /* ========================================
                       MODERN REPORT STYLES - v3.1.0
                       Enhanced typography, cards, and visual hierarchy
                       ======================================== */

                    /* Content Wrapper - Main Container */
                    .report-section-content .content-wrapper {
                        display: flex;
                        flex-direction: column;
                        gap: 1.25rem;
                    }

                    /* Insights Section - Card Container */
                    .report-section-content .insights-section {
                        margin-bottom: 0;
                        border-radius: 16px;
                        backdrop-filter: blur(12px);
                        background: linear-gradient(145deg, rgba(255,255,255,0.85), rgba(255,255,255,0.65));
                        border: 1px solid rgba(255,255,255,0.8);
                        box-shadow:
                            0 4px 6px -1px rgba(0,0,0,0.05),
                            0 10px 15px -3px rgba(0,0,0,0.08),
                            inset 0 1px 0 rgba(255,255,255,0.6);
                        overflow: hidden;
                        transition: all 0.25s ease;
                    }

                    .report-section-content .insights-section:hover {
                        box-shadow:
                            0 8px 12px -2px rgba(0,0,0,0.08),
                            0 16px 24px -4px rgba(0,0,0,0.1),
                            inset 0 1px 0 rgba(255,255,255,0.7);
                        transform: translateY(-1px);
                    }

                    /* Section Header */
                    .report-section-content .insights-section h3 {
                        font-size: 0.9375rem;
                        font-weight: 700;
                        color: #0F172A;
                        margin: 0;
                        padding: 1rem 1.25rem;
                        background: linear-gradient(135deg, rgba(6,142,117,0.08) 0%, rgba(6,142,117,0.03) 100%);
                        border-bottom: 1px solid rgba(6,142,117,0.12);
                        display: flex;
                        align-items: center;
                        gap: 0.625rem;
                        letter-spacing: -0.01em;
                    }

                    .report-section-content .insights-section h3::before {
                        content: "";
                        width: 4px;
                        height: 18px;
                        background: linear-gradient(180deg, #068E75, #057A63);
                        border-radius: 2px;
                        flex-shrink: 0;
                    }

                    .report-section-content .insights-section h3 .section-icon {
                        width: 18px;
                        height: 18px;
                        object-fit: contain;
                        flex-shrink: 0;
                    }

                    /* Section Content Area */
                    .report-section-content .insights-section > p,
                    .report-section-content .insights-section > div:not(.metrics-grid):not(.insights-list) {
                        padding: 1rem 1.25rem;
                        margin: 0;
                    }

                    /* Paragraph Styling */
                    .report-section-content .insights-section p {
                        font-size: 0.8125rem;
                        color: #475569;
                        line-height: 1.75;
                        margin: 0 0 0.75rem 0;
                        letter-spacing: 0.01em;
                    }

                    .report-section-content .insights-section p:last-child {
                        margin-bottom: 0;
                    }

                    .report-section-content .insights-section p + p {
                        margin-top: 0.5rem;
                    }

                    /* Insights List - Bullet Points */
                    .report-section-content .insights-list {
                        list-style: none;
                        padding: 0.75rem 1rem;
                        margin: 0;
                        display: flex;
                        flex-direction: column;
                        gap: 0.625rem;
                    }

                    .report-section-content .insights-list li {
                        padding: 0.875rem 1rem;
                        margin-bottom: 0;
                        border-radius: 12px;
                        background: linear-gradient(135deg, rgba(255,255,255,0.7) 0%, rgba(255,255,255,0.5) 100%);
                        border: 1px solid rgba(6,142,117,0.1);
                        box-shadow: 0 2px 4px rgba(0,0,0,0.03);
                        transition: all 0.2s ease;
                        position: relative;
                    }

                    .report-section-content .insights-list li:hover {
                        background: linear-gradient(135deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.65) 100%);
                        border-color: rgba(6,142,117,0.2);
                        box-shadow: 0 4px 8px rgba(0,0,0,0.06);
                        transform: translateX(2px);
                    }

                    .report-section-content .insights-list li::before {
                        display: none;
                    }

                    .report-section-content .insights-list li:last-child {
                        margin-bottom: 0;
                    }

                    /* Item Content Layout */
                    .report-section-content .item-content {
                        display: flex;
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 0.625rem;
                    }

                    .report-section-content .item-content .metric-badge {
                        order: -1;
                    }

                    .report-section-content .item-text {
                        flex: 1;
                        font-size: 0.8125rem;
                        color: #374151;
                        line-height: 1.6;
                    }

                    .report-section-content .item-text strong {
                        font-weight: 600;
                        color: #0F172A;
                        letter-spacing: -0.01em;
                    }

                    /* Metric Badges - Status Pills */
                    .report-section-content .metric-badge {
                        display: inline-flex;
                        align-items: center;
                        padding: 0.3125rem 0.75rem;
                        border-radius: 20px;
                        font-size: 0.6875rem;
                        font-weight: 600;
                        white-space: nowrap;
                        letter-spacing: 0.02em;
                        text-transform: uppercase;
                        flex-shrink: 0;
                        box-shadow: 0 1px 2px rgba(0,0,0,0.05);
                    }

                    .report-section-content .metric-badge.critical {
                        background: linear-gradient(135deg, #FEE2E2, #FECACA);
                        color: #991B1B;
                        border: 1px solid #FCA5A5;
                    }

                    .report-section-content .metric-badge.warning {
                        background: linear-gradient(135deg, #FEF9C3, #FEF08A);
                        color: #854D0E;
                        border: 1px solid #FDE68A;
                    }

                    .report-section-content .metric-badge.info {
                        background: linear-gradient(135deg, #DBEAFE, #BFDBFE);
                        color: #1E40AF;
                        border: 1px solid #93C5FD;
                    }

                    .report-section-content .metric-badge.good {
                        background: linear-gradient(135deg, #D1FAE5, #A7F3D0);
                        color: #065F46;
                        border: 1px solid #6EE7B7;
                    }

                    /* Metrics Grid - Card Layout */
                    .report-section-content .metrics-grid {
                        display: grid;
                        grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
                        gap: 0.875rem;
                        padding: 1rem 1.25rem 1.25rem;
                    }

                    /* Metric Card - Individual Stats */
                    .report-section-content .metric-card {
                        padding: 1rem;
                        border-radius: 14px;
                        background: linear-gradient(145deg, rgba(255,255,255,0.9), rgba(255,255,255,0.7));
                        border: 1px solid rgba(6,142,117,0.12);
                        box-shadow:
                            0 2px 4px rgba(0,0,0,0.04),
                            0 4px 8px rgba(0,0,0,0.06);
                        transition: all 0.25s ease;
                        display: flex;
                        flex-direction: column;
                        gap: 0.375rem;
                        position: relative;
                        overflow: hidden;
                    }

                    .report-section-content .metric-card::before {
                        content: "";
                        position: absolute;
                        top: 0;
                        left: 0;
                        right: 0;
                        height: 3px;
                        background: linear-gradient(90deg, #068E75, #10B981);
                        opacity: 0;
                        transition: opacity 0.25s ease;
                    }

                    .report-section-content .metric-card:hover {
                        transform: translateY(-3px);
                        box-shadow:
                            0 4px 8px rgba(0,0,0,0.06),
                            0 8px 16px rgba(6,142,117,0.12);
                        border-color: rgba(6,142,117,0.25);
                    }

                    .report-section-content .metric-card:hover::before {
                        opacity: 1;
                    }

                    .report-section-content .metric-card .metric-badge {
                        align-self: flex-start;
                        margin-bottom: 0.5rem;
                    }

                    .report-section-content .metric-value {
                        font-size: 1.375rem;
                        font-weight: 700;
                        color: #0F172A;
                        line-height: 1.2;
                        letter-spacing: -0.02em;
                    }

                    .report-section-content .metric-label {
                        font-size: 0.75rem;
                        color: #64748B;
                        line-height: 1.4;
                        font-weight: 500;
                    }

                    /* Data Tables in Reports */
                    .report-section-content .insights-section table {
                        width: 100%;
                        border-collapse: separate;
                        border-spacing: 0;
                        margin: 0.75rem 0;
                        font-size: 0.8125rem;
                        border-radius: 10px;
                        overflow: hidden;
                        box-shadow: 0 2px 6px rgba(0,0,0,0.04);
                    }

                    .report-section-content .insights-section th {
                        background: linear-gradient(135deg, rgba(6,142,117,0.12) 0%, rgba(6,142,117,0.08) 100%);
                        color: #065F46;
                        padding: 0.75rem 1rem;
                        text-align: left;
                        font-weight: 600;
                        font-size: 0.75rem;
                        text-transform: uppercase;
                        letter-spacing: 0.03em;
                        border-bottom: 2px solid rgba(6,142,117,0.2);
                    }

                    .report-section-content .insights-section td {
                        padding: 0.75rem 1rem;
                        border-bottom: 1px solid rgba(0,0,0,0.05);
                        color: #374151;
                        background: rgba(255,255,255,0.5);
                    }

                    .report-section-content .insights-section tr:last-child td {
                        border-bottom: none;
                    }

                    .report-section-content .insights-section tr:hover td {
                        background: rgba(6,142,117,0.04);
                    }

                    /* Inline Code in Reports */
                    .report-section-content .insights-section code {
                        background: linear-gradient(135deg, rgba(6,142,117,0.1), rgba(6,142,117,0.06));
                        color: #065F46;
                        padding: 0.125rem 0.5rem;
                        border-radius: 6px;
                        font-size: 0.75rem;
                        font-family: 'SF Mono', Monaco, 'Consolas', monospace;
                        font-weight: 500;
                        border: 1px solid rgba(6,142,117,0.15);
                    }

                    /* Strong/Bold Text Enhancement */
                    .report-section-content .insights-section strong {
                        font-weight: 600;
                        color: #0F172A;
                    }

                    /* Emphasis/Italic Text */
                    .report-section-content .insights-section em {
                        font-style: italic;
                        color: #475569;
                    }

                    /* Horizontal Rule */
                    .report-section-content .insights-section hr {
                        border: none;
                        height: 1px;
                        background: linear-gradient(90deg, transparent, rgba(6,142,117,0.2), transparent);
                        margin: 1rem 0;
                    }

                    /* Blockquote Styling */
                    .report-section-content .insights-section blockquote {
                        margin: 1rem 0;
                        padding: 0.875rem 1rem;
                        background: linear-gradient(135deg, rgba(6,142,117,0.06), rgba(6,142,117,0.02));
                        border-left: 4px solid #068E75;
                        border-radius: 0 10px 10px 0;
                        font-style: italic;
                        color: #475569;
                    }

                    /* Numbers and Percentages Highlight */
                    .report-section-content .highlight-number {
                        font-weight: 700;
                        color: #068E75;
                        font-feature-settings: 'tnum';
                    }

                    .report-section-content .highlight-positive {
                        color: #059669;
                        font-weight: 600;
                    }

                    .report-section-content .highlight-negative {
                        color: #DC2626;
                        font-weight: 600;
                    }
                    `}
                </style>
                <div className="h-screen flex flex-col relative font-sans text-gray-800 bg-gradient-custom">
                    <div className="absolute -right-10 -top-10 w-50 h-50 blur-[40px] pointer-events-none"
                        style={{ background: 'radial-gradient(circle at center, rgba(134, 239, 172, 0.3), transparent 70%)' }}></div>
                    <div className="absolute -left-8 -bottom-8 w-38 h-38 blur-[40px] pointer-events-none"
                        style={{ background: 'radial-gradient(circle at center, rgba(167, 243, 208, 0.3), transparent 70%)' }}></div>

                    <SpreetailHeader />

                    {/* Scrollable content - everything scrolls together */}
                    <div className="flex-1 overflow-y-auto px-5 pb-5 min-h-0 hide-scrollbar">
                        <div className="max-w-md mx-auto pt-3">
                            {/* Header */}
                            <div className="mb-4">
                                <div className="flex items-start justify-between mb-2">
                                    <h1 className="text-[#0F172A] text-lg font-semibold m-0">
                                        Analysis Results
                                    </h1>
                                    <button
                                        onClick={onReset}
                                        className="text-xs px-3 py-1.5 bg-white/60 border border-gray-200 rounded-md cursor-pointer hover:bg-white/80 transition-colors"
                                    >
                                        New Analysis
                                    </button>
                                </div>
                                <p className="text-[#667085] text-xs m-0">
                                    "{userQuestion.length > 50 ? userQuestion.substring(0, 50) + "..." : userQuestion}"
                                </p>
                            </div>

                            {/* Render Results Section */}
                            <div className="data-tab-container">
                                    {/* Plan Steps Section - Parent Transparent Card */}
                                    {enabledSteps.length > 0 && (
                                        <div className="data-section-card">
                                            {/* Section Header */}
                                            <div className="data-section-header">
                                                <h2 className="data-section-title">Render Results</h2>
                                                <span className="data-section-badge">
                                                    {selectedSteps.size} of {enabledSteps.length} Selected
                                                </span>
                                            </div>

                                            {/* Steps List */}
                                            <div className="data-steps-list">
                                                {enabledSteps.map((step) => (
                                                    <div key={step.step_id} className="data-step-item">
                                                        <div
                                                            className={`data-step-card ${
                                                                stepResults[step.step_id] === "success" ? "executed" :
                                                                stepResults[step.step_id] === "error" ? "error" : ""
                                                            } ${executingStep === step.step_id ? "executing" : ""}`}
                                                        >
                                                            {/* Checkbox */}
                                                            <label className="data-checkbox-wrapper">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedSteps.has(step.step_id)}
                                                                    onChange={() => toggleStepSelection(step.step_id)}
                                                                    disabled={isRenderingAll || isWritingData}
                                                                    className="data-checkbox-input"
                                                                />
                                                                <span className="data-checkbox-box">
                                                                    <svg className="data-checkbox-check" viewBox="0 0 12 12" fill="none">
                                                                        <path d="M2 6L5 9L10 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                                    </svg>
                                                                </span>
                                                            </label>

                                                            {/* Step Content Area */}
                                                            <div className="data-step-main">
                                                                {/* Header Row: Icon + Title */}
                                                                <div className="data-step-header-row">
                                                                    <div className="data-step-icon">
                                                                        {getStepIcon(step.step_id)}
                                                                    </div>
                                                                    <h4 className="data-step-title">{getStepTitle(step.step_id)}</h4>
                                                                </div>

                                                                {/* Description Box - separate background */}
                                                                {step.reason && (
                                                                    <div className="data-step-description-box">
                                                                        <p className="data-step-description-text">{step.reason}</p>
                                                                    </div>
                                                                )}

                                                                {/* Grid Type Custom Dropdown - all pivot_table steps */}
                                                                {getBaseStepType(step.step_id) === "pivot_table" && (
                                                                    <div className="data-step-grid-select">
                                                                        <span className="dropdown-label">Grid Type:</span>
                                                                        <div className="dropdown-container">
                                                                            <button
                                                                                type="button"
                                                                                className="dropdown-button"
                                                                                onClick={() => setOpenDropdownId(openDropdownId === step.step_id ? null : step.step_id)}
                                                                                disabled={isRenderingAll || isWritingData}
                                                                            >
                                                                                <span className="dropdown-text">
                                                                                    {getGridTypeForStep(step.step_id) === "pivot" ? "Pivot Grid" : "Flattened Grid"}
                                                                                </span>
                                                                                <svg 
                                                                                    width="16" 
                                                                                    height="16" 
                                                                                    viewBox="0 0 16 16" 
                                                                                    fill="none"
                                                                                    className={`dropdown-arrow ${openDropdownId === step.step_id ? 'open' : ''}`}
                                                                                >
                                                                                    <path d="M4 6L8 10L12 6" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                                                </svg>
                                                                            </button>
                                                                            {openDropdownId === step.step_id && (
                                                                                <div className="dropdown-menu">
                                                                                    <button
                                                                                        type="button"
                                                                                        className={`dropdown-option ${getGridTypeForStep(step.step_id) === "pivot" ? "selected" : ""}`}
                                                                                        onClick={() => {
                                                                                            setGridTypeForStep(step.step_id, "pivot");
                                                                                            setOpenDropdownId(null);
                                                                                        }}
                                                                                    >
                                                                                        Pivot Grid
                                                                                    </button>
                                                                                    {(() => {
                                                                                        const { groupedGrid } = getPivotTableDataForStep(step.step_id);
                                                                                        return groupedGrid && groupedGrid.length > 0 ? (
                                                                                            <button
                                                                                                type="button"
                                                                                                className={`dropdown-option ${getGridTypeForStep(step.step_id) === "grouped" ? "selected" : ""}`}
                                                                                                onClick={() => {
                                                                                                    setGridTypeForStep(step.step_id, "grouped");
                                                                                                    setOpenDropdownId(null);
                                                                                                }}
                                                                                            >
                                                                                                Flattened Grid
                                                                                            </button>
                                                                                        ) : null;
                                                                                    })()}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {/* Status Indicator */}
                                                            {executingStep === step.step_id && (
                                                                <div className="data-step-spinner" />
                                                            )}
                                                            {stepResults[step.step_id] === "success" && (
                                                                <CheckCircleIcon sx={{ fontSize: 16, color: "#068E75" }} />
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Render Button */}
                                            <div className="data-render-btn-wrapper">
                                                <button
                                                    className="data-render-btn"
                                                    onClick={handleRenderSelected}
                                                    disabled={isRenderingAll || isWritingData || selectedSteps.size === 0}
                                                >
                                                    {isRenderingAll ? (
                                                        <>
                                                            <div className="data-btn-spinner" />
                                                            <span>Rendering...</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <PlayArrowIcon sx={{ fontSize: 18 }} />
                                                            <span>Render Selected ({selectedSteps.size})</span>
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Success/Error Messages */}
                                    {writeSuccess && (
                                        <div className="data-message data-message-success">
                                            <svg className="data-message-icon" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                            </svg>
                                            <span>{writeSuccess}</span>
                                        </div>
                                    )}
                                    {writeError && (
                                        <div ref={writeErrorRef} className="bg-red-100 rounded-md p-3 mb-4 flex items-start gap-2">
                                            <div className="w-5 h-5 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                                <span className="text-white text-sm font-bold">!</span>
                                            </div>
                                            <p className="text-[13px] text-red-800 m-0 leading-snug">{writeError}</p>
                                        </div>
                                    )}
                                </div>
                        </div>
                    </div>
                </div>
            </>
        );
    };

    export default RCAResultsView;
